import { type ButtonHTMLAttributes, forwardRef } from "react";
import { interactionSoundProps } from "../interaction-sound";
import { cn } from "../lib/cn";

/**
 * Switch toggle, design-system spec: 38x22 track, 18x18 thumb, teal-700 on,
 * gray-300 off. 220ms ease for both background and thumb translate. The
 * canonical control for channel preferences, quiet hours, the demo panel.
 */
export interface SwitchProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onChange"> {
  checked: boolean;
  onCheckedChange?: (next: boolean) => void;
  label?: string;
}

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { checked, onCheckedChange, label, className, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      // Inherits the "toggle" cue from the interaction-sound taxonomy.
      {...interactionSoundProps("toggle")}
      onClick={() => onCheckedChange?.(!checked)}
      className={cn(
        "relative h-[22px] w-[38px] shrink-0 rounded-full border-0 p-0 transition-colors duration-200 ease-[var(--ease-out-expo)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600",
        // The 22px track stays; the pointer target grows to the 24px WCAG 2.5.8 floor.
        "after:absolute after:inset-x-0 after:-top-px after:-bottom-px after:content-['']",
        checked ? "bg-teal-700" : "bg-gray-300",
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden="true"
        className="absolute top-[2px] left-[2px] h-[18px] w-[18px] rounded-full bg-white shadow-[0_1px_2px_color-mix(in_srgb,var(--color-navy)_20%,transparent)] transition-transform duration-200 ease-[var(--ease-out-expo)]"
        style={{ transform: checked ? "translateX(16px)" : "translateX(0)" }}
      />
    </button>
  );
});
